/**
 * Définition des statuts de rendez-vous et règles de transition
 * 
 * WORKFLOW DES STATUTS:
 * 
 * 1. PENDING (En attente)
 *    - Créé automatiquement lors de la prise de RDV
 *    - Transitions possibles: PENDING_PAYMENT, CANCELLED
 * 
 * 2. PENDING_PAYMENT (Attente paiement)
 *    - Après soumission du RDV, en attente de paiement
 *    - Transitions possibles: CONFIRMED (après paiement), CANCELLED
 * 
 * 3. CONFIRMED (Confirmé)
 *    - Après paiement validé
 *    - Transitions possibles: IN_PROGRESS (médecin démarre), CANCELLED
 * 
 * 4. IN_PROGRESS (En cours)
 *    - Consultation démarrée par le médecin
 *    - Transitions possibles: COMPLETED (médecin termine)
 * 
 * 5. COMPLETED (Terminé)
 *    - Consultation terminée par le médecin
 *    - État final - pas de transition
 * 
 * 6. CANCELLED (Annulé)
 *    - Annulé par patient, médecin ou admin
 *    - État final - pas de transition
 * 
 * QUI PEUT MODIFIER QUOI:
 * 
 * | Action                | Patient | Médecin | Admin |
 * |-----------------------|---------|---------|-------|
 * | Créer RDV             |    ✓    |         |       |
 * | Annuler RDV           |    ✓*   |    ✓    |   ✓   |
 * | Démarrer consultation |         |    ✓    |       |
 * | Terminer consultation |         |    ✓    |   ✓   |
 * | Confirmer paiement    |         |         |   ✓   |
 * 
 * * Patient peut annuler uniquement si status != IN_PROGRESS et != COMPLETED
 */

// Définition des statuts
export const APPOINTMENT_STATUS = {
  PENDING: 'pending',
  PENDING_PAYMENT: 'pending_payment',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

// Labels en français
export const STATUS_LABELS = {
  [APPOINTMENT_STATUS.PENDING]: 'En attente',
  [APPOINTMENT_STATUS.PENDING_PAYMENT]: 'Attente paiement',
  [APPOINTMENT_STATUS.CONFIRMED]: 'Confirmé',
  [APPOINTMENT_STATUS.IN_PROGRESS]: 'En cours',
  [APPOINTMENT_STATUS.COMPLETED]: 'Terminé',
  [APPOINTMENT_STATUS.CANCELLED]: 'Annulé'
};

// Couleurs des badges (classes CSS)
export const STATUS_COLORS = {
  [APPOINTMENT_STATUS.PENDING]: 'pending',
  [APPOINTMENT_STATUS.PENDING_PAYMENT]: 'pending_payment',
  [APPOINTMENT_STATUS.CONFIRMED]: 'confirmed',
  [APPOINTMENT_STATUS.IN_PROGRESS]: 'in-progress',
  [APPOINTMENT_STATUS.COMPLETED]: 'completed',
  [APPOINTMENT_STATUS.CANCELLED]: 'cancelled'
};

// Statuts de paiement
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  REFUNDED: 'refunded'
};

export const PAYMENT_LABELS = {
  [PAYMENT_STATUS.PENDING]: 'En attente',
  [PAYMENT_STATUS.PAID]: 'Payé',
  [PAYMENT_STATUS.REFUNDED]: 'Remboursé'
};

/**
 * Obtenir le label d'un statut
 */
export function getStatusLabel(status) {
  return STATUS_LABELS[status] || status || 'Inconnu';
}

/**
 * Obtenir le label de paiement
 */
export function getPaymentLabel(paymentStatus) {
  return PAYMENT_LABELS[paymentStatus] || paymentStatus || 'Inconnu';
}

/**
 * Vérifier si le patient peut annuler un RDV
 */
export function canPatientCancel(status) {
  return [
    APPOINTMENT_STATUS.PENDING,
    APPOINTMENT_STATUS.PENDING_PAYMENT,
    APPOINTMENT_STATUS.CONFIRMED
  ].includes(status);
}

/**
 * Vérifier si le médecin peut démarrer une consultation
 */
export function canDoctorStart(status) {
  return status === APPOINTMENT_STATUS.CONFIRMED;
}

/**
 * Vérifier si le médecin peut terminer une consultation
 */
export function canDoctorComplete(status) {
  return [
    APPOINTMENT_STATUS.CONFIRMED,
    APPOINTMENT_STATUS.IN_PROGRESS
  ].includes(status);
}

/**
 * Vérifier si l'admin peut terminer une consultation
 */
export function canAdminComplete(status) {
  return [
    APPOINTMENT_STATUS.CONFIRMED,
    APPOINTMENT_STATUS.IN_PROGRESS
  ].includes(status);
}

/**
 * Vérifier si le RDV est modifiable
 */
export function isAppointmentModifiable(status) {
  return ![
    APPOINTMENT_STATUS.COMPLETED,
    APPOINTMENT_STATUS.CANCELLED
  ].includes(status);
}

/**
 * Vérifier si le patient peut rejoindre une téléconsultation
 */
export function isTeleconsultationService(service) {
  if (!service) return false;
  if (service.service_id === 'teleconsultation') return true;
  return (service.name || '').toLowerCase().includes('téléconsult');
}

export function canJoinTeleconsultation(status, service) {
  return isTeleconsultationService(service) && [
    APPOINTMENT_STATUS.CONFIRMED,
    APPOINTMENT_STATUS.IN_PROGRESS
  ].includes(status);
}
